// cfoArView — pure, framework-free transforms for the native `/cfo/ar` page.
// Everything the page renders (period selection, aging buckets, change drivers,
// customer/invoice projections, sorting, collectability aggregates, the
// Reported↔Adjusted basis, the AI evidence bag, and the Excel column shapes)
// lives here so it can be unit-tested without React. The page stays a thin
// renderer over these helpers and reuses `applyQuickBooksArTrendExclusions` from
// the legacy engine so adjusted-basis math matches the classic A/R Review.

import type {
  QuickBooksArCollectabilityAnalysis,
  QuickBooksArCollectabilityCustomer,
  QuickBooksArCollectabilityInvoice,
  QuickBooksArCustomerConcern,
  QuickBooksArIncomeAccountAllocation,
  QuickBooksArInvoiceDrilldown,
  QuickBooksArTrendAnalysis,
  QuickBooksArTrendPeriod,
} from '../../../data/ServerApiClient'
import { applyQuickBooksArTrendExclusions } from '../quickBooksArAnalysis'
import { formatCurrencyCompact, formatCurrencyExact } from './cfoFormat'
import type { AgingBucket } from './charts'
import type { CfoArExcelColumn } from './cfoArExcelExport'

const num = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const numberOrNull = (value: unknown): number | null => {
  if (value == null || (typeof value === 'string' && !value.trim())) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// ---------------------------------------------------------------------------
// Formatters (full-precision table variants; the compact hero reuses cfoFormat)
// ---------------------------------------------------------------------------

export const formatArCurrency = (value: number | null | undefined): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 'N/A'
  const abs = formatCurrencyExact(Math.abs(n))
  return n < 0 ? `(${abs})` : abs
}

export const formatArSignedCurrency = (value: number | null | undefined): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 'N/A'
  if (Math.abs(n) < 0.5) return formatCurrencyExact(0)
  const abs = formatCurrencyExact(Math.abs(n))
  return n < 0 ? `-${abs}` : `+${abs}`
}

export const formatArDays = (value: number | null | undefined): string => {
  const n = Number(value)
  if (value == null || !Number.isFinite(n)) return 'N/A'
  return `${Math.round(n)} days`
}

export const formatArDayNumber = (value: number | null | undefined): string => {
  const n = Number(value)
  if (value == null || !Number.isFinite(n)) return 'N/A'
  return String(Math.round(n))
}

export const formatArPercent = (value: number | null | undefined): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 'N/A'
  return `${(n * 100).toFixed(1)}%`
}

// ---------------------------------------------------------------------------
// Customer-key + exclusion helpers (case-insensitive, matches the legacy engine)
// ---------------------------------------------------------------------------

export const arCustomerExclusionKey = (value: unknown): string => String(value ?? '').trim().toLowerCase()

export const arCustomerKey = (customer: { customerId?: string | null; customer?: string | null }): string =>
  String(customer?.customerId || customer?.customer || '').trim()

export const arExcludedKeySet = (keys: string[]): Set<string> =>
  new Set((keys || []).map(arCustomerExclusionKey).filter(Boolean))

export const arCustomerIsExcluded = (
  customer: { customerId?: string | null; customer?: string | null },
  excluded: Set<string>,
): boolean =>
  excluded.has(arCustomerExclusionKey(customer?.customerId)) || excluded.has(arCustomerExclusionKey(customer?.customer))

// ---------------------------------------------------------------------------
// Basis (Reported ↔ Adjusted)
// ---------------------------------------------------------------------------

export type ArBasis = 'reported' | 'adjusted'

/**
 * Apply the Adjusted basis by removing excluded customers via the legacy
 * `applyQuickBooksArTrendExclusions` (no raw rows on the page — the cached
 * rollforward is subtracted client-side). Returns the base analysis unchanged
 * for the Reported basis or when nothing is excluded.
 */
export function applyArBasis(
  base: QuickBooksArTrendAnalysis | null,
  basis: ArBasis,
  excludedKeys: string[],
): QuickBooksArTrendAnalysis | null {
  if (!base) return base
  if (basis !== 'adjusted' || !excludedKeys.length) return base
  return applyQuickBooksArTrendExclusions(base, excludedKeys, []) || base
}

// ---------------------------------------------------------------------------
// Period selection + bucket helpers
// ---------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Short month label for a period (e.g. "Apr 2026"), keyed off its end date. */
export const formatArTrendPeriodLabel = (period: QuickBooksArTrendPeriod | null | undefined): string => {
  const raw = String((period as any)?.periodEnd ?? (period as any)?.periodStart ?? (period as any)?.period ?? '').slice(0, 10)
  const match = /^(\d{4})-(\d{2})/.exec(raw)
  if (!match) return raw || ''
  const month = Number(match[2])
  if (month < 1 || month > 12) return raw
  return `${MONTHS[month - 1]} ${match[1]}`
}

const periodStartOf = (period: QuickBooksArTrendPeriod | null | undefined): string =>
  String((period as any)?.periodStart ?? (period as any)?.period ?? '').slice(0, 10)

const periodEndOf = (period: QuickBooksArTrendPeriod | null | undefined): string =>
  String((period as any)?.periodEnd ?? '').slice(0, 10)

/**
 * ISO end date (YYYY-MM-DD) a period actually covers, keyed off its end date and
 * falling back to its start. Used to describe the true "data through" month when
 * the requested/selected header period runs ahead of the A/R artifact's coverage.
 */
export const arPeriodEndDate = (period: QuickBooksArTrendPeriod | null | undefined): string =>
  String((period as any)?.periodEnd ?? (period as any)?.periodStart ?? (period as any)?.period ?? '').slice(0, 10)

export type ArPeriodSelection = {
  periods: QuickBooksArTrendPeriod[]
  metricPeriod: QuickBooksArTrendPeriod | null
  previousMetricPeriod: QuickBooksArTrendPeriod | null
}

export function selectArPeriod(
  analysis: QuickBooksArTrendAnalysis | null,
  selectedPeriodStart: string,
): ArPeriodSelection {
  const periods = analysis?.periods || []
  if (!periods.length) return { periods, metricPeriod: null, previousMetricPeriod: null }
  const wanted = String(selectedPeriodStart || '').slice(0, 10)
  const found = wanted ? periods.findIndex((period) => periodStartOf(period) === wanted) : -1
  const metricIdx = found >= 0 ? found : periods.length - 1
  return {
    periods,
    metricPeriod: periods[metricIdx] || null,
    previousMetricPeriod: metricIdx > 0 ? periods[metricIdx - 1] : null,
  }
}

/**
 * Canonical headline DSO anchor — mirrors the server's
 * `headline_dso_from_analysis`: the newest period that actually carries a DSO
 * (skipping a trailing partial/current month whose DSO is still null), plus the
 * next-newest period's DSO. The A/R page header renders this so it equals the
 * DSO instant card, CFO Home `ar_summary`, and the firm cockpit — all of which
 * anchor to the newest CLOSED month. Using the raw last period instead makes the
 * header silently diverge the moment a partial current month appears.
 */
export function arHeadlineDsoAnchor(analysis: QuickBooksArTrendAnalysis | null): {
  period: QuickBooksArTrendPeriod | null
  dso: number | null
  prior: number | null
} {
  const periods = analysis?.periods || []
  // A DSO that is JSON `null`/`undefined` means "no DSO for this period" (a
  // trailing partial month) and must be skipped — mirroring the server's
  // `value_num`, which treats null as absent but keeps 0. Note `numberOrNull`
  // alone is not enough: `Number(null) === 0`, so a null DSO would read as 0.
  const periodDso = (period: QuickBooksArTrendPeriod | undefined): number | null =>
    period == null || period.dso == null ? null : numberOrNull(period.dso)
  for (let i = periods.length - 1; i >= 0; i--) {
    const value = periodDso(periods[i])
    if (value != null) {
      let prior: number | null = null
      for (let j = i - 1; j >= 0; j--) {
        const priorValue = periodDso(periods[j])
        if (priorValue != null) {
          prior = priorValue
          break
        }
      }
      return { period: periods[i], dso: value, prior }
    }
  }
  return { period: null, dso: null, prior: null }
}

const UNRELIABLE_AGING_SOURCES = new Set(['unreconciled_activity', 'unreconciled_aging_snapshot'])

export const arBucketSplitReliable = (period: QuickBooksArTrendPeriod | null | undefined): boolean =>
  !UNRELIABLE_AGING_SOURCES.has(String((period as any)?.agingSource || '').toLowerCase())

export const arPeriodHasBucketDetail = (period: QuickBooksArTrendPeriod | null | undefined): boolean =>
  [period?.currentAr, period?.under90Ar, period?.over90Ar].some((value) => value != null && Number.isFinite(Number(value)))

export const arBucketSplitUsable = (period: QuickBooksArTrendPeriod | null | undefined): boolean =>
  arBucketSplitReliable(period) || arPeriodHasBucketDetail(period)

export type ArBucketAmounts = { current: number; under90: number; over90: number; overdue: number }

/**
 * Aging buckets scaled proportionally to the period's A/R control balance, as in
 * the legacy A/R Review chart: when the summed bucket detail exceeds the
 * controlled ending A/R, the buckets are shrunk to fit so the stacked column
 * never overstates the financial balance. When the buckets sum to less than the
 * ending A/R, the remainder is shown as the unallocated/discrepancy segment.
 */
export function periodVisibleBucketAmounts(period: QuickBooksArTrendPeriod | null | undefined): ArBucketAmounts {
  const current = Math.max(0, num(period?.currentAr))
  const under90 = Math.max(0, num(period?.under90Ar))
  const over90 = Math.max(0, num(period?.over90Ar))
  const bucketTotal = current + under90 + over90
  const endingAr = Math.max(0, num(period?.endingAr))
  const scale = bucketTotal > 0.005 && endingAr > 0 ? Math.min(bucketTotal, endingAr) / bucketTotal : 1
  const scaledCurrent = current * scale
  const scaledUnder90 = under90 * scale
  const scaledOver90 = over90 * scale
  const overdue = period?.overdueAr != null ? Math.max(0, num(period.overdueAr)) * scale : scaledUnder90 + scaledOver90
  return { current: scaledCurrent, under90: scaledUnder90, over90: scaledOver90, overdue }
}

/** Aging buckets for the overview chart (Current / 1-90 / 90+ [+ missing/unclassified]). */
export function arAgingBuckets(period: QuickBooksArTrendPeriod | null | undefined): AgingBucket[] {
  if (!period) return []
  const out: AgingBucket[] = [
    { label: 'Current', value: Math.max(0, num(period.currentAr)), tone: 'good' },
    { label: '1-90', value: Math.max(0, num(period.under90Ar)), tone: 'watch' },
    { label: '90+', value: Math.max(0, num(period.over90Ar)), tone: 'risk' },
  ]
  const missingDue = Math.max(0, num(period.missingDueAr))
  const unclassified = Math.max(0, num((period as any).unclassifiedAr))
  if (missingDue > 0.005) out.push({ label: 'Missing due', value: missingDue, tone: 'watch' })
  if (unclassified > 0.005) out.push({ label: 'Unclassified', value: unclassified, tone: 'watch' })
  return out
    .filter((bucket) => bucket.value > 0.005)
    .map((bucket) => ({ ...bucket, display: formatCurrencyCompact(bucket.value) }))
}

export type ArAgingBridgeBucket = AgingBucket & {
  id: string
  startValue: number
  endValue: number
  delta: number
}

export type ArAgingBridge = {
  startLabel: string
  endLabel: string
  startTotal: number
  endTotal: number
  totalDelta: number
  buckets: ArAgingBridgeBucket[]
}

const AR_AGING_BUCKET_DEFS: Array<{ id: string; label: string; tone: AgingBucket['tone']; value: (period: QuickBooksArTrendPeriod) => number }> = [
  { id: 'current', label: 'Current', tone: 'good', value: (period) => num(period.currentAr) },
  { id: 'under90', label: '1-90', tone: 'watch', value: (period) => num(period.under90Ar) },
  { id: 'over90', label: '90+', tone: 'risk', value: (period) => num(period.over90Ar) },
  { id: 'missingDue', label: 'Missing due', tone: 'watch', value: (period) => num(period.missingDueAr) },
  { id: 'unclassified', label: 'Unclassified', tone: 'watch', value: (period) => num((period as any).unclassifiedAr) },
]

const arBridgeBucketValues = (period: QuickBooksArTrendPeriod): Map<string, AgingBucket> => {
  const endingAr = Math.max(0, num(period.endingAr))
  const buckets = AR_AGING_BUCKET_DEFS.map((def) => ({
    id: def.id,
    label: def.label,
    tone: def.tone,
    value: Math.max(0, def.value(period)),
  }))
  const rawTotal = buckets.reduce((sum, bucket) => sum + bucket.value, 0)
  if (endingAr > 0.005 && rawTotal > endingAr + 0.5) {
    const scale = endingAr / rawTotal
    for (const bucket of buckets) bucket.value *= scale
  } else if (endingAr > rawTotal + 0.5) {
    const existing = buckets.find((bucket) => bucket.id === 'unclassified')
    if (existing) existing.value += endingAr - rawTotal
  }
  return new Map(
    buckets
      .filter((bucket) => bucket.value > 0.005)
      .map((bucket) => [
        bucket.id,
        {
          label: bucket.label,
          value: bucket.value,
          tone: bucket.tone,
          display: formatCurrencyCompact(bucket.value),
        },
      ]),
  )
}

export function buildArAgingBridge(
  startPeriod: QuickBooksArTrendPeriod | null | undefined,
  endPeriod: QuickBooksArTrendPeriod | null | undefined,
): ArAgingBridge | null {
  if (!startPeriod || !endPeriod) return null
  const startValues = arBridgeBucketValues(startPeriod)
  const endValues = arBridgeBucketValues(endPeriod)
  const ids = AR_AGING_BUCKET_DEFS.map((bucket) => bucket.id).filter((id) => startValues.has(id) || endValues.has(id))
  if (!ids.length) return null
  const buckets = ids.map((id) => {
    const def = AR_AGING_BUCKET_DEFS.find((bucket) => bucket.id === id)!
    const startValue = startValues.get(id)?.value || 0
    const endValue = endValues.get(id)?.value || 0
    const delta = endValue - startValue
    return {
      id,
      label: def.label,
      tone: def.tone,
      value: Math.abs(delta),
      startValue,
      endValue,
      delta,
      display: formatCurrencyCompact(Math.abs(delta)),
    }
  })
  const startTotal = buckets.reduce((sum, bucket) => sum + bucket.startValue, 0)
  const endTotal = buckets.reduce((sum, bucket) => sum + bucket.endValue, 0)
  return {
    startLabel: formatArTrendPeriodLabel(startPeriod),
    endLabel: formatArTrendPeriodLabel(endPeriod),
    startTotal,
    endTotal,
    totalDelta: endTotal - startTotal,
    buckets,
  }
}

// ---------------------------------------------------------------------------
// Change drivers
// ---------------------------------------------------------------------------

export type ArCustomerChangeDriver = {
  customerId: string
  customer: string
  previousEndingAr: number
  endingAr: number
  endingDelta: number
  currentDelta: number
  under90Delta: number
  over90Delta: number
  dsoDelta: number | null
}

const customerNameMap = (analysis: QuickBooksArTrendAnalysis | null): Map<string, string> =>
  new Map(
    (analysis?.customerTrends || []).map((customer) => [
      arCustomerKey(customer),
      customer.customer || 'Unassigned customer',
    ]),
  )

export function buildArCustomerChangeDrivers(
  analysis: QuickBooksArTrendAnalysis | null,
  metricPeriod: QuickBooksArTrendPeriod | null,
  previousMetricPeriod: QuickBooksArTrendPeriod | null,
  excluded?: Set<string> | null,
): ArCustomerChangeDriver[] {
  if (!analysis || !metricPeriod || !previousMetricPeriod) return []
  const trends = analysis.customerPeriodTrends || {}
  const curStart = periodStartOf(metricPeriod)
  const prevStart = periodStartOf(previousMetricPeriod)
  const names = customerNameMap(analysis)
  const drivers: ArCustomerChangeDriver[] = []
  for (const [customerId, customerPeriods] of Object.entries(trends)) {
    const name = names.get(customerId) || customerId
    if (excluded && (excluded.has(arCustomerExclusionKey(customerId)) || excluded.has(arCustomerExclusionKey(name)))) continue
    const arr = Array.isArray(customerPeriods) ? customerPeriods : []
    const cur = arr.find((period) => periodStartOf(period) === curStart)
    const prev = arr.find((period) => periodStartOf(period) === prevStart)
    const endingAr = num(cur?.endingAr)
    const previousEndingAr = num(prev?.endingAr)
    const endingDelta = endingAr - previousEndingAr
    if (Math.abs(endingDelta) < 1) continue
    const customer = names.get(customerId) || String((cur as any)?.customer || (prev as any)?.customer || customerId) || 'Unassigned customer'
    drivers.push({
      customerId,
      customer,
      previousEndingAr,
      endingAr,
      endingDelta,
      currentDelta: num(cur?.currentAr) - num(prev?.currentAr),
      under90Delta: num(cur?.under90Ar) - num(prev?.under90Ar),
      over90Delta: num(cur?.over90Ar) - num(prev?.over90Ar),
      dsoDelta: cur?.dso != null && prev?.dso != null ? num(cur.dso) - num(prev.dso) : null,
    })
  }
  return drivers.sort((a, b) => Math.abs(b.endingDelta) - Math.abs(a.endingDelta)).slice(0, 12)
}

// ---------------------------------------------------------------------------
// Customer rows projected onto a period
// ---------------------------------------------------------------------------

export function arCustomerRowsForPeriod(
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
): QuickBooksArCustomerConcern[] {
  if (!analysis) return []
  const trends = analysis.customerPeriodTrends || {}
  const entries = Object.entries(trends)
  if (!entries.length) return analysis.customerTrends || []
  const names = customerNameMap(analysis)
  const start = periodStartOf(period)
  const end = periodEndOf(period)
  const rows: QuickBooksArCustomerConcern[] = []
  for (const [customerId, customerPeriods] of entries) {
    const arr = Array.isArray(customerPeriods) ? customerPeriods : []
    const match =
      (start ? arr.find((candidate) => periodStartOf(candidate) === start) : null) ||
      (end ? arr.find((candidate) => periodEndOf(candidate) === end) : null) ||
      arr[arr.length - 1]
    if (!match) continue
    const endingAr = num(match.endingAr)
    const overdueAr = num(match.overdueAr)
    const over60Ar = num(match.over60Ar)
    const weightedAverageAge = match.weightedAverageAge == null ? null : num(match.weightedAverageAge)
    rows.push({
      customerId,
      customer: names.get(customerId) || String((match as any).customer || customerId) || 'Unassigned customer',
      endingAr,
      overdueAr,
      over60Ar,
      over90Ar: num(match.over90Ar),
      dso: match.dso == null ? null : num(match.dso),
      weightedAverageAge,
      riskScore: overdueAr + over60Ar * 1.5 + endingAr * Math.min(2, Math.max(0, num(weightedAverageAge)) / 365),
      openInvoiceCount: (match as any).openInvoiceCount == null ? null : num((match as any).openInvoiceCount),
    })
  }
  return rows
}

// ---------------------------------------------------------------------------
// Invoice drilldown rows for a selected period + customer
// ---------------------------------------------------------------------------

export function arInvoiceRowsForSelection(
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
  selectedCustomerKey: string,
): QuickBooksArInvoiceDrilldown[] {
  if (!analysis) return []
  const source = (analysis.periodInvoiceDrilldowns?.length ? analysis.periodInvoiceDrilldowns : analysis.invoiceDrilldowns) || []
  const targetEnd = periodEndOf(period)
  const targetStart = periodStartOf(period)
  let rows = source
  if (period && (targetEnd || targetStart)) {
    rows = rows.filter((invoice) => {
      const invEnd = String((invoice as any).periodEnd || '').slice(0, 10)
      const invStart = String((invoice as any).periodStart || '').slice(0, 10)
      if (!invEnd && !invStart) return true
      return (targetEnd && invEnd === targetEnd) || (targetStart && invStart === targetStart)
    })
  }
  const customerKey = String(selectedCustomerKey || '').trim()
  if (customerKey) {
    rows = rows.filter(
      (invoice) =>
        arCustomerKey(invoice) === customerKey ||
        String((invoice as any).customerId || '') === customerKey ||
        String((invoice as any).customer || '') === customerKey,
    )
  }
  return rows
}

export const quickBooksArInvoiceOver90Amount = (invoice: QuickBooksArInvoiceDrilldown): number =>
  num((invoice as any)?.daysPastDue) > 90 ? Math.max(0, num((invoice as any)?.openAmount)) : 0

// ---------------------------------------------------------------------------
// Customer + income-account allocations
// ---------------------------------------------------------------------------

export type ArIncomeAccountOption = {
  id: string
  name: string
  openAmount: number
  rowCount: number
  unclassified?: boolean
}

export type ArIncomeAccountAllocationSortColumn =
  | 'customer'
  | 'invoiceNumber'
  | 'invoiceDate'
  | 'dueDate'
  | 'incomeAccountName'
  | 'serviceItemName'
  | 'className'
  | 'openAmount'
  | 'currentAmount'
  | 'oneToThirtyAmount'
  | 'thirtyOneToSixtyAmount'
  | 'sixtyOneToNinetyAmount'
  | 'overNinetyAmount'
  | 'daysPastDue'

export type ArIncomeAccountColumnId =
  | 'customer'
  | 'invoiceNumber'
  | 'invoiceDate'
  | 'dueDate'
  | 'incomeAccountName'
  | 'serviceItemName'
  | 'className'
  | 'openAmount'
  | 'currentAmount'
  | 'oneToThirtyAmount'
  | 'thirtyOneToSixtyAmount'
  | 'sixtyOneToNinetyAmount'
  | 'overNinetyAmount'
  | 'daysPastDue'
  | 'agingBucket'
  | 'unclassifiedReason'

export type ArIncomeAccountReportView = {
  rows: QuickBooksArIncomeAccountAllocation[]
  allRows: QuickBooksArIncomeAccountAllocation[]
  accountOptions: ArIncomeAccountOption[]
  selectedAccountIds: Set<string>
  filtered: boolean
  totals: {
    allOpenAmount: number
    filteredOpenAmount: number
    currentAmount: number
    oneToThirtyAmount: number
    thirtyOneToSixtyAmount: number
    sixtyOneToNinetyAmount: number
    overNinetyAmount: number
    unclassifiedAmount: number
    reconciliationVariance: number
  }
}

export const AR_UNCLASSIFIED_INCOME_ACCOUNT_ID = '__unclassified__'
export const AR_UNCLASSIFIED_INCOME_ACCOUNT_NAME = 'No income account'

export const arIncomeAccountKey = (row: Pick<QuickBooksArIncomeAccountAllocation, 'incomeAccountId' | 'incomeAccountName'>): string =>
  String(row.incomeAccountId || row.incomeAccountName || AR_UNCLASSIFIED_INCOME_ACCOUNT_ID).trim() || AR_UNCLASSIFIED_INCOME_ACCOUNT_ID

export const arIncomeAccountIsUnclassified = (
  row: Pick<QuickBooksArIncomeAccountAllocation, 'incomeAccountId' | 'incomeAccountName'>,
): boolean =>
  arIncomeAccountKey(row) === AR_UNCLASSIFIED_INCOME_ACCOUNT_ID

export const arIncomeAccountDisplayName = (
  row: Pick<QuickBooksArIncomeAccountAllocation, 'incomeAccountId' | 'incomeAccountName'>,
): string =>
  arIncomeAccountIsUnclassified(row)
    ? AR_UNCLASSIFIED_INCOME_ACCOUNT_NAME
    : String(row.incomeAccountName || row.incomeAccountId || AR_UNCLASSIFIED_INCOME_ACCOUNT_NAME)

export function arIncomeAccountOptions(
  rows: QuickBooksArIncomeAccountAllocation[],
): ArIncomeAccountOption[] {
  const byId = new Map<string, ArIncomeAccountOption>()
  for (const row of rows) {
    const id = arIncomeAccountKey(row)
    const existing = byId.get(id)
    const unclassified = arIncomeAccountIsUnclassified(row)
    const name = arIncomeAccountDisplayName(row)
    if (existing) {
      existing.openAmount += num(row.openAmount)
      existing.rowCount += 1
      if (!existing.name && name) existing.name = name
    } else {
      byId.set(id, {
        id,
        name,
        openAmount: num(row.openAmount),
        rowCount: 1,
        unclassified,
      })
    }
  }
  return [...byId.values()].sort((a, b) => {
    if (a.unclassified !== b.unclassified) return a.unclassified ? 1 : -1
    return Math.abs(b.openAmount) - Math.abs(a.openAmount) || a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  })
}

const arAllocationRowsForPeriod = (
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
): QuickBooksArIncomeAccountAllocation[] => {
  const targetEnd = periodEndOf(period)
  const targetStart = periodStartOf(period)
  const source = (analysis as any)?.periodIncomeAccountAllocations as QuickBooksArIncomeAccountAllocation[] | undefined
  return (Array.isArray(source) ? source : []).filter((row) => {
    const rowEnd = String((row as any).periodEnd || '').slice(0, 10)
    const rowStart = String((row as any).periodStart || '').slice(0, 10)
    if (!targetEnd && !targetStart) return true
    return (targetEnd && rowEnd === targetEnd) || (targetStart && rowStart === targetStart)
  })
}

export function arIncomeAccountRowsForPeriod(
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
  selectedAccountIds: Set<string> | null,
): ArIncomeAccountReportView {
  const allRows = arAllocationRowsForPeriod(analysis, period)
  const accountOptions = arIncomeAccountOptions(allRows)
  const selected = selectedAccountIds && selectedAccountIds.size ? selectedAccountIds : null
  const rows = selected ? allRows.filter((row) => selected.has(arIncomeAccountKey(row))) : allRows
  const totals = {
    allOpenAmount: sumIncomeRows(allRows, 'openAmount'),
    filteredOpenAmount: sumIncomeRows(rows, 'openAmount'),
    currentAmount: sumIncomeRows(rows, 'currentAmount'),
    oneToThirtyAmount: sumIncomeRows(rows, 'oneToThirtyAmount'),
    thirtyOneToSixtyAmount: sumIncomeRows(rows, 'thirtyOneToSixtyAmount'),
    sixtyOneToNinetyAmount: sumIncomeRows(rows, 'sixtyOneToNinetyAmount'),
    overNinetyAmount: sumIncomeRows(rows, 'overNinetyAmount'),
    unclassifiedAmount: rows.reduce((sum, row) => sum + (arIncomeAccountIsUnclassified(row) ? num(row.openAmount) : 0), 0),
    reconciliationVariance: num(period?.endingAr) - sumIncomeRows(allRows, 'openAmount'),
  }
  return {
    rows,
    allRows,
    accountOptions,
    selectedAccountIds: selected ? new Set(selected) : new Set(),
    filtered: Boolean(selected),
    totals,
  }
}

const sumIncomeRows = (rows: QuickBooksArIncomeAccountAllocation[], key: keyof QuickBooksArIncomeAccountAllocation): number =>
  rows.reduce((sum, row) => sum + num(row[key]), 0)

export function arIncomeAccountTrendPeriods(
  analysis: QuickBooksArTrendAnalysis | null,
  periods: QuickBooksArTrendPeriod[],
  selectedAccountIds: Set<string> | null,
): QuickBooksArTrendPeriod[] {
  const selected = selectedAccountIds && selectedAccountIds.size ? selectedAccountIds : null
  if (!selected) return periods
  const source = (analysis as any)?.periodIncomeAccountAllocations as QuickBooksArIncomeAccountAllocation[] | undefined
  const rows = (Array.isArray(source) ? source : []).filter((row) => selected.has(arIncomeAccountKey(row)))
  const byPeriod = new Map<string, QuickBooksArIncomeAccountAllocation[]>()
  for (const row of rows) {
    const keys = [
      String((row as any).periodEnd || '').slice(0, 10),
      String((row as any).periodStart || '').slice(0, 10),
    ].filter(Boolean)
    for (const key of keys) {
      const existing = byPeriod.get(key)
      if (existing) existing.push(row)
      else byPeriod.set(key, [row])
    }
  }
  return periods.map((period) => {
    const key = periodEndOf(period) || periodStartOf(period)
    const periodRows = byPeriod.get(key) || []
    const currentAr = sumIncomeRows(periodRows, 'currentAmount')
    const oneToThirty = sumIncomeRows(periodRows, 'oneToThirtyAmount')
    const thirtyOneToSixty = sumIncomeRows(periodRows, 'thirtyOneToSixtyAmount')
    const sixtyOneToNinety = sumIncomeRows(periodRows, 'sixtyOneToNinetyAmount')
    const under90Ar = oneToThirty + thirtyOneToSixty + sixtyOneToNinety
    const over90Ar = sumIncomeRows(periodRows, 'overNinetyAmount')
    const endingAr = sumIncomeRows(periodRows, 'openAmount')
    const ageDenominator = periodRows.reduce((sum, row) => {
      const amount = Math.max(0, num(row.openAmount))
      const daysOpen = num(row.daysOpen)
      return daysOpen > 0 ? sum + amount : sum
    }, 0)
    const weightedAverageAge = ageDenominator > 0
      ? periodRows.reduce((sum, row) => sum + Math.max(0, num(row.openAmount)) * Math.max(0, num(row.daysOpen)), 0) / ageDenominator
      : null
    return {
      ...period,
      endingAr,
      currentAr,
      under90Ar,
      over90Ar,
      overdueAr: under90Ar + over90Ar,
      over60Ar: sixtyOneToNinety + over90Ar,
      missingDueAr: 0,
      sourceBucketTotalAr: endingAr,
      agingBucketTotalAr: endingAr,
      unclassifiedAr: periodRows.reduce((sum, row) => sum + (arIncomeAccountIsUnclassified(row) ? num(row.openAmount) : 0), 0),
      sourceExcessAr: 0,
      bucketReconciliationAr: 0,
      knownReconcilingDifferenceAr: 0,
      agingSource: 'income_account_allocation_subset',
      invoiceSales: null,
      dsoSales: null,
      dsoSalesSource: null,
      averageDso: null,
      dso: null,
      weightedAverageAge,
    }
  })
}

// ---------------------------------------------------------------------------
// Income-account filter applied to the customer / change-driver / invoice tables
// ---------------------------------------------------------------------------
//
// When the page's income-account selection is active, the customer-facing tables
// are rebuilt from the same per-invoice-line allocation rows that power the
// "Customers & income accounts" report, so every table on the page reflects the
// same selected slice of A/R. DSO has no per-account meaning, so it goes null on
// the filtered rows (matching the filtered trend).

type ArAllocationCustomerAggregate = {
  customerId: string
  customer: string
  openAmount: number
  currentAmount: number
  under90Amount: number
  over60Amount: number
  over90Amount: number
  ageNumerator: number
  ageDenominator: number
  invoiceKeys: Set<string>
}

const arSelectedAllocationRows = (
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
  selectedAccountIds: Set<string>,
): QuickBooksArIncomeAccountAllocation[] =>
  arAllocationRowsForPeriod(analysis, period).filter((row) => selectedAccountIds.has(arIncomeAccountKey(row)))

function arAllocationCustomerAggregates(
  rows: QuickBooksArIncomeAccountAllocation[],
): Map<string, ArAllocationCustomerAggregate> {
  const byCustomer = new Map<string, ArAllocationCustomerAggregate>()
  for (const row of rows) {
    const key = arCustomerKey({ customerId: row.customerId, customer: row.customer }) || 'Unassigned customer'
    let agg = byCustomer.get(key)
    if (!agg) {
      agg = {
        customerId: String(row.customerId || '').trim() || key,
        customer: String(row.customer || '').trim() || 'Unassigned customer',
        openAmount: 0,
        currentAmount: 0,
        under90Amount: 0,
        over60Amount: 0,
        over90Amount: 0,
        ageNumerator: 0,
        ageDenominator: 0,
        invoiceKeys: new Set<string>(),
      }
      byCustomer.set(key, agg)
    }
    const open = num(row.openAmount)
    const sixtyOneToNinety = num(row.sixtyOneToNinetyAmount)
    const overNinety = num(row.overNinetyAmount)
    agg.openAmount += open
    agg.currentAmount += num(row.currentAmount)
    agg.under90Amount += num(row.oneToThirtyAmount) + num(row.thirtyOneToSixtyAmount) + sixtyOneToNinety
    agg.over60Amount += sixtyOneToNinety + overNinety
    agg.over90Amount += overNinety
    const daysOpen = num(row.daysOpen)
    if (daysOpen > 0) {
      const weight = Math.max(0, open)
      agg.ageNumerator += weight * daysOpen
      agg.ageDenominator += weight
    }
    if (row.invoiceKey) agg.invoiceKeys.add(String(row.invoiceKey))
  }
  return byCustomer
}

/** Customer aging rows rebuilt from the selected income accounts' allocation rows. */
export function arIncomeAccountCustomerRowsForPeriod(
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
  selectedAccountIds: Set<string>,
  excluded?: Set<string> | null,
): QuickBooksArCustomerConcern[] {
  const rows: QuickBooksArCustomerConcern[] = []
  for (const agg of arAllocationCustomerAggregates(arSelectedAllocationRows(analysis, period, selectedAccountIds)).values()) {
    if (excluded && arCustomerIsExcluded(agg, excluded)) continue
    const weightedAverageAge = agg.ageDenominator > 0 ? agg.ageNumerator / agg.ageDenominator : null
    const overdueAr = agg.under90Amount + agg.over90Amount
    rows.push({
      customerId: agg.customerId,
      customer: agg.customer,
      endingAr: agg.openAmount,
      overdueAr,
      over60Ar: agg.over60Amount,
      over90Ar: agg.over90Amount,
      dso: null,
      weightedAverageAge,
      riskScore: overdueAr + agg.over60Amount * 1.5 + agg.openAmount * Math.min(2, Math.max(0, num(weightedAverageAge)) / 365),
      openInvoiceCount: agg.invoiceKeys.size || null,
    })
  }
  return rows
}

/** Change drivers recomputed period-over-period from the selected accounts' allocation rows. */
export function arIncomeAccountChangeDrivers(
  analysis: QuickBooksArTrendAnalysis | null,
  metricPeriod: QuickBooksArTrendPeriod | null,
  previousMetricPeriod: QuickBooksArTrendPeriod | null,
  selectedAccountIds: Set<string>,
  excluded?: Set<string> | null,
): ArCustomerChangeDriver[] {
  if (!analysis || !metricPeriod || !previousMetricPeriod) return []
  const cur = arAllocationCustomerAggregates(arSelectedAllocationRows(analysis, metricPeriod, selectedAccountIds))
  const prev = arAllocationCustomerAggregates(arSelectedAllocationRows(analysis, previousMetricPeriod, selectedAccountIds))
  const drivers: ArCustomerChangeDriver[] = []
  for (const key of new Set([...cur.keys(), ...prev.keys()])) {
    const c = cur.get(key)
    const p = prev.get(key)
    const customerId = c?.customerId || p?.customerId || key
    const customer = c?.customer || p?.customer || key
    if (excluded && (excluded.has(arCustomerExclusionKey(customerId)) || excluded.has(arCustomerExclusionKey(customer)))) continue
    const endingAr = c?.openAmount || 0
    const previousEndingAr = p?.openAmount || 0
    const endingDelta = endingAr - previousEndingAr
    if (Math.abs(endingDelta) < 1) continue
    drivers.push({
      customerId,
      customer,
      previousEndingAr,
      endingAr,
      endingDelta,
      currentDelta: (c?.currentAmount || 0) - (p?.currentAmount || 0),
      under90Delta: (c?.under90Amount || 0) - (p?.under90Amount || 0),
      over90Delta: (c?.over90Amount || 0) - (p?.over90Amount || 0),
      dsoDelta: null,
    })
  }
  return drivers.sort((a, b) => Math.abs(b.endingDelta) - Math.abs(a.endingDelta)).slice(0, 12)
}

/**
 * Restrict invoice drilldown rows to invoices carrying at least one allocation
 * line in the selected income accounts. Membership filter only — open amounts
 * stay whole-invoice; the Customers & income accounts table is the allocated view.
 */
export function arFilterInvoicesByIncomeAccounts(
  invoices: QuickBooksArInvoiceDrilldown[],
  analysis: QuickBooksArTrendAnalysis | null,
  period: QuickBooksArTrendPeriod | null,
  selectedAccountIds: Set<string> | null,
): QuickBooksArInvoiceDrilldown[] {
  if (!selectedAccountIds || !selectedAccountIds.size) return invoices
  const keys = new Set(
    arSelectedAllocationRows(analysis, period, selectedAccountIds)
      .map((row) => String(row.invoiceKey || ''))
      .filter(Boolean),
  )
  return invoices.filter((invoice) => keys.has(String((invoice as any).invoiceKey || '')))
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export type ArSortDirection = 'asc' | 'desc'
export type ArSortState<C extends string> = { columnId: C; direction: ArSortDirection }

const compareArSortValues = (a: number | string | null | undefined, b: number | string | null | undefined): number => {
  const aNum = typeof a === 'number' ? a : Number(a)
  const bNum = typeof b === 'number' ? b : Number(b)
  const aIsNum = Number.isFinite(aNum) && a !== '' && a != null
  const bIsNum = Number.isFinite(bNum) && b !== '' && b != null
  if (aIsNum && bIsNum) return aNum - bNum
  if (aIsNum) return -1
  if (bIsNum) return 1
  // Match the legacy A/R Review collation: natural number ordering ("INV-2"
  // before "INV-10") and case-insensitive folding.
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' })
}

const sortByValue = <T>(
  rows: T[],
  direction: ArSortDirection,
  getValue: (row: T) => number | string | null | undefined,
  tiebreakers: Array<(row: T) => number | string | null | undefined> = [],
): T[] => {
  const factor = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const primary = compareArSortValues(getValue(a), getValue(b))
    if (primary !== 0) return primary * factor
    for (const tiebreak of tiebreakers) {
      const cmp = compareArSortValues(tiebreak(a), tiebreak(b))
      if (cmp !== 0) return cmp
    }
    return 0
  })
}

export type ArCustomerSortColumn = 'customer' | 'endingAr' | 'overdueAr' | 'over90Ar' | 'dso'

export const arCustomerSortValue = (customer: QuickBooksArCustomerConcern, columnId: ArCustomerSortColumn): number | string | null => {
  switch (columnId) {
    case 'customer': return customer.customer || ''
    case 'endingAr': return num(customer.endingAr)
    case 'overdueAr': return num(customer.overdueAr)
    case 'over90Ar': return num(customer.over90Ar)
    case 'dso': return customer.dso == null ? null : num(customer.dso)
    default: return null
  }
}

export const sortArCustomers = (rows: QuickBooksArCustomerConcern[], sort: ArSortState<ArCustomerSortColumn>): QuickBooksArCustomerConcern[] =>
  sortByValue(rows, sort.direction, (row) => arCustomerSortValue(row, sort.columnId), [
    (row) => Math.abs(num(row.endingAr)),
    (row) => row.customer || '',
  ])

export type ArInvoiceSortColumn = 'invoiceNumber' | 'customer' | 'invoiceDate' | 'dueDate' | 'openAmount' | 'over90Amount' | 'daysPastDue'

export const arInvoiceSortValue = (invoice: QuickBooksArInvoiceDrilldown, columnId: ArInvoiceSortColumn): number | string | null => {
  switch (columnId) {
    case 'invoiceNumber': return invoice.invoiceNumber || invoice.invoiceKey || ''
    case 'customer': return invoice.customer || ''
    case 'invoiceDate': return invoice.invoiceDate || ''
    case 'dueDate': return invoice.dueDate || ''
    case 'openAmount': return num(invoice.openAmount)
    case 'over90Amount': return quickBooksArInvoiceOver90Amount(invoice)
    case 'daysPastDue': return invoice.daysPastDue == null ? null : num(invoice.daysPastDue)
    default: return null
  }
}

export const sortArInvoices = (rows: QuickBooksArInvoiceDrilldown[], sort: ArSortState<ArInvoiceSortColumn>): QuickBooksArInvoiceDrilldown[] =>
  sortByValue(rows, sort.direction, (row) => arInvoiceSortValue(row, sort.columnId), [
    (row) => num(row.openAmount),
    (row) => row.invoiceNumber || row.invoiceKey || '',
  ])

export const arIncomeAccountAllocationSortValue = (
  row: QuickBooksArIncomeAccountAllocation,
  columnId: ArIncomeAccountAllocationSortColumn,
): number | string | null => {
  switch (columnId) {
    case 'customer': return row.customer || ''
    case 'invoiceNumber': return row.invoiceNumber || row.invoiceKey || ''
    case 'invoiceDate': return row.invoiceDate || ''
    case 'dueDate': return row.dueDate || ''
    case 'incomeAccountName': return row.incomeAccountName || row.incomeAccountId || ''
    case 'serviceItemName': return row.serviceItemName || ''
    case 'className': return row.className || ''
    case 'daysPastDue': return row.daysPastDue == null ? null : num(row.daysPastDue)
    default: return num((row as any)[columnId])
  }
}

export const sortArIncomeAccountAllocations = (
  rows: QuickBooksArIncomeAccountAllocation[],
  sort: ArSortState<ArIncomeAccountAllocationSortColumn>,
): QuickBooksArIncomeAccountAllocation[] =>
  sortByValue(rows, sort.direction, (row) => arIncomeAccountAllocationSortValue(row, sort.columnId), [
    (row) => row.customer || '',
    (row) => row.incomeAccountName || row.incomeAccountId || '',
    (row) => row.agingBucket || '',
    (row) => row.invoiceNumber || row.invoiceKey || '',
  ])

export type ArCollectabilityCustomerSortColumn =
  | 'customer' | 'openAmount' | 'over90Amount' | 'over180Amount' | 'over270Amount' | 'over360Amount' | 'expectedWriteOffAmount' | 'worstPastDueDays'

export const arCollectabilityCustomerSortValue = (
  customer: QuickBooksArCollectabilityCustomer,
  columnId: ArCollectabilityCustomerSortColumn,
): number | string | null => {
  switch (columnId) {
    case 'customer': return customer.customer || ''
    case 'worstPastDueDays': return customer.worstPastDueDays == null ? null : num(customer.worstPastDueDays)
    default: return num((customer as any)[columnId])
  }
}

export const sortArCollectabilityCustomers = (
  rows: QuickBooksArCollectabilityCustomer[],
  sort: ArSortState<ArCollectabilityCustomerSortColumn>,
): QuickBooksArCollectabilityCustomer[] =>
  sortByValue(rows, sort.direction, (row) => arCollectabilityCustomerSortValue(row, sort.columnId), [
    (row) => num(row.expectedWriteOffAmount),
    (row) => num(row.over360Amount),
    (row) => row.customer || '',
  ])

export type ArCollectabilityInvoiceSortColumn =
  | 'invoiceNumber' | 'customer' | 'dueDate' | 'openAmount' | 'daysPastDue' | 'thresholdDays' | 'benchmarkRecoveryRate' | 'expectedWriteOffAmount'

export const arCollectabilityInvoiceSortValue = (
  invoice: QuickBooksArCollectabilityInvoice,
  columnId: ArCollectabilityInvoiceSortColumn,
): number | string | null => {
  switch (columnId) {
    case 'invoiceNumber': return invoice.invoiceNumber || invoice.invoiceKey || ''
    case 'customer': return invoice.customer || ''
    case 'dueDate': return invoice.dueDate || ''
    case 'daysPastDue': return invoice.daysPastDue == null ? null : num(invoice.daysPastDue)
    case 'thresholdDays': return invoice.thresholdDays == null ? null : num(invoice.thresholdDays)
    case 'benchmarkRecoveryRate': return invoice.benchmarkRecoveryRate == null ? null : num(invoice.benchmarkRecoveryRate)
    default: return num((invoice as any)[columnId])
  }
}

export const sortArCollectabilityInvoices = (
  rows: QuickBooksArCollectabilityInvoice[],
  sort: ArSortState<ArCollectabilityInvoiceSortColumn>,
): QuickBooksArCollectabilityInvoice[] =>
  sortByValue(rows, sort.direction, (row) => arCollectabilityInvoiceSortValue(row, sort.columnId), [
    (row) => num(row.expectedWriteOffAmount),
    (row) => num(row.openAmount),
    (row) => row.invoiceNumber || row.invoiceKey || '',
  ])

// ---------------------------------------------------------------------------
// Collectability aggregates (basis-aware)
// ---------------------------------------------------------------------------

export type ArCollectabilityView = {
  customers: QuickBooksArCollectabilityCustomer[]
  invoices: QuickBooksArCollectabilityInvoice[]
  totalAtRiskOpen: number
  benchmarkedAtRiskOpen: number
  totalExpectedCollectible: number
  totalExpectedWriteOff: number
  over360Exposure: number
  hasBenchmarks: boolean
  weightedRecoveryRate: number | null
}

export function arCollectabilityView(
  collectability: QuickBooksArCollectabilityAnalysis | null | undefined,
  excluded: Set<string> | null,
  basisAdjusted: boolean,
): ArCollectabilityView {
  if (!basisAdjusted && collectability?.summaryTotals) {
    return { ...collectability.summaryTotals, customers: [], invoices: [] }
  }
  const allCustomers = collectability?.customers || []
  const allInvoices = collectability?.currentInvoices || []
  const applyFilter = basisAdjusted && excluded && excluded.size > 0
  const customers = applyFilter ? allCustomers.filter((customer) => !arCustomerIsExcluded(customer, excluded!)) : allCustomers
  const invoices = applyFilter ? allInvoices.filter((invoice) => !arCustomerIsExcluded(invoice, excluded!)) : allInvoices
  let totalAtRiskOpen = 0
  let benchmarkedAtRiskOpen = 0
  let totalExpectedCollectible = 0
  let totalExpectedWriteOff = 0
  let over360Exposure = 0
  for (const invoice of invoices) {
    const open = Math.max(0, num(invoice.openAmount))
    totalAtRiskOpen += open
    if (Number.isFinite(Number(invoice.benchmarkRecoveryRate))) benchmarkedAtRiskOpen += open
    totalExpectedCollectible += Math.max(0, num(invoice.expectedCollectibleAmount))
    totalExpectedWriteOff += Math.max(0, num(invoice.expectedWriteOffAmount))
    if (num(invoice.thresholdDays) >= 360) over360Exposure += open
  }
  const hasBenchmarks = benchmarkedAtRiskOpen > 0.005
  return {
    customers,
    invoices,
    totalAtRiskOpen,
    benchmarkedAtRiskOpen,
    totalExpectedCollectible,
    totalExpectedWriteOff,
    over360Exposure,
    hasBenchmarks,
    weightedRecoveryRate: hasBenchmarks ? totalExpectedCollectible / benchmarkedAtRiskOpen : null,
  }
}

// ---------------------------------------------------------------------------
// Excluded-customer summary (drives the Adjusted-basis banner)
// ---------------------------------------------------------------------------

export type ArExcludedSummaryItem = { customerId: string; customer: string; endingAr: number }

export function arExcludedSummary(analysis: QuickBooksArTrendAnalysis | null): ArExcludedSummaryItem[] {
  return (analysis?.excludedCustomers || []).map((customer) => ({
    customerId: String(customer.customerId || ''),
    customer: customer.customer || 'Unassigned customer',
    endingAr: num(customer.endingAr),
  }))
}

export const arExcludedArAmount = (analysis: QuickBooksArTrendAnalysis | null): number =>
  arExcludedSummary(analysis).reduce((sum, customer) => sum + Math.max(0, customer.endingAr), 0)

// ---------------------------------------------------------------------------
// Reserve signal (overview tile + AI evidence)
// ---------------------------------------------------------------------------

export type ArReserveSignal = {
  recommendedReserveAmount: number | null
  recommendedPercentOfSales: number | null
  recommendedMethod: string | null
  expectedWriteOff: number
  atRiskOpen: number
  hasBenchmarks: boolean
}

export function arReserveSignal(
  collectability: QuickBooksArCollectabilityAnalysis | null | undefined,
  view: ArCollectabilityView,
): ArReserveSignal {
  const reserve = collectability?.badDebtReserve || null
  return {
    recommendedReserveAmount: reserve?.recommendedReserveAmount == null ? null : num(reserve.recommendedReserveAmount),
    recommendedPercentOfSales: reserve?.recommendedPercentOfSales == null ? null : num(reserve.recommendedPercentOfSales),
    recommendedMethod: reserve?.recommendedMethod || null,
    expectedWriteOff: view.totalExpectedWriteOff,
    atRiskOpen: view.totalAtRiskOpen,
    hasBenchmarks: view.hasBenchmarks,
  }
}

// ---------------------------------------------------------------------------
// AI evidence selection (passed through to CfoInteriorPage → analysis modal)
// ---------------------------------------------------------------------------

export function buildArAnalysisSelection(args: {
  sourceTable: string
  asOf: string | null
  basis: ArBasis
  section: string
  selectedPeriodEnd: string | null
  buckets: AgingBucket[]
  topRisks: QuickBooksArCustomerConcern[]
  excluded: ArExcludedSummaryItem[]
  excludedArAmount: number
  reserveSignal: ArReserveSignal
}): Record<string, any> {
  return {
    evidence_scope: 'ar_collectability',
    source_table: args.sourceTable,
    as_of: args.asOf,
    basis: args.basis,
    section: args.section,
    selected_period: args.selectedPeriodEnd,
    buckets: args.buckets,
    top_customers: args.topRisks.slice(0, 5).map((customer) => ({
      customer: customer.customer,
      customerId: customer.customerId || null,
      endingAr: numberOrNull(customer.endingAr),
      over90Ar: numberOrNull(customer.over90Ar),
    })),
    top_risks: args.topRisks.slice(0, 5).map((customer) => ({
      customer: customer.customer,
      endingAr: numberOrNull(customer.endingAr),
      overdueAr: numberOrNull(customer.overdueAr),
      over90Ar: numberOrNull(customer.over90Ar),
      riskScore: numberOrNull(customer.riskScore),
    })),
    reserve_signal: {
      recommended_reserve: args.reserveSignal.recommendedReserveAmount,
      recommended_percent_of_sales: args.reserveSignal.recommendedPercentOfSales,
      recommended_method: args.reserveSignal.recommendedMethod,
      expected_write_off: args.reserveSignal.expectedWriteOff,
      at_risk_open: args.reserveSignal.atRiskOpen,
    },
    excluded_customers: args.excluded.map((customer) => ({
      customer: customer.customer,
      customerId: customer.customerId || null,
      endingAr: customer.endingAr,
    })),
    excluded_ar_amount: args.excludedArAmount,
  }
}

// ---------------------------------------------------------------------------
// Excel column shapes (carried forward verbatim from the legacy panels)
// ---------------------------------------------------------------------------

export const AR_CUSTOMER_EXCEL_COLUMNS: CfoArExcelColumn<QuickBooksArCustomerConcern>[] = [
  { header: 'Customer', value: (customer) => customer.customer || '', widthPx: 240 },
  { header: 'Open A/R', value: (customer) => num(customer.endingAr), format: 'currency', widthPx: 115 },
  { header: 'Overdue A/R', value: (customer) => num(customer.overdueAr), format: 'currency', widthPx: 115 },
  { header: '>90 Days', value: (customer) => num(customer.over90Ar), format: 'currency', widthPx: 115 },
  { header: 'DSO', value: (customer) => (customer.dso == null ? null : num(customer.dso)), format: 'days', widthPx: 85 },
]

export const AR_INVOICE_EXCEL_COLUMNS: CfoArExcelColumn<QuickBooksArInvoiceDrilldown>[] = [
  { header: 'Invoice', value: (invoice) => invoice.invoiceNumber || invoice.invoiceKey || '', widthPx: 130 },
  { header: 'Customer', value: (invoice) => invoice.customer || '', widthPx: 240 },
  { header: 'Invoice date', value: (invoice) => invoice.invoiceDate || '', widthPx: 110 },
  { header: 'Due date', value: (invoice) => invoice.dueDate || '', widthPx: 110 },
  { header: 'Open A/R', value: (invoice) => num(invoice.openAmount), format: 'currency', widthPx: 115 },
  { header: '>90 Days', value: (invoice) => quickBooksArInvoiceOver90Amount(invoice), format: 'currency', widthPx: 115 },
  { header: 'Past due days', value: (invoice) => (invoice.daysPastDue == null ? null : num(invoice.daysPastDue)), format: 'days', widthPx: 110 },
]

export const AR_INCOME_ACCOUNT_EXCEL_COLUMNS: CfoArExcelColumn<QuickBooksArIncomeAccountAllocation>[] = [
  { header: 'Customer', value: (row) => row.customer || '', widthPx: 240 },
  { header: 'Invoice', value: (row) => row.invoiceNumber || row.invoiceKey || '', widthPx: 130 },
  { header: 'Invoice date', value: (row) => row.invoiceDate || '', widthPx: 110 },
  { header: 'Due date', value: (row) => row.dueDate || '', widthPx: 110 },
  { header: 'Income account', value: (row) => row.incomeAccountName || row.incomeAccountId || '', widthPx: 220 },
  { header: 'Service item', value: (row) => row.serviceItemName || '', widthPx: 150 },
  { header: 'Class', value: (row) => row.className || '', widthPx: 140 },
  { header: 'Aging bucket', value: (row) => row.agingBucket || '', widthPx: 115 },
  { header: 'Open A/R', value: (row) => num(row.openAmount), format: 'currency', widthPx: 115 },
  { header: 'Current', value: (row) => num(row.currentAmount), format: 'currency', widthPx: 105 },
  { header: '1-30', value: (row) => num(row.oneToThirtyAmount), format: 'currency', widthPx: 105 },
  { header: '31-60', value: (row) => num(row.thirtyOneToSixtyAmount), format: 'currency', widthPx: 105 },
  { header: '61-90', value: (row) => num(row.sixtyOneToNinetyAmount), format: 'currency', widthPx: 105 },
  { header: '90+', value: (row) => num(row.overNinetyAmount), format: 'currency', widthPx: 105 },
  { header: 'Past due days', value: (row) => (row.daysPastDue == null ? null : num(row.daysPastDue)), format: 'days', widthPx: 110 },
  { header: 'Unclassified reason', value: (row) => row.unclassifiedReason || '', widthPx: 180 },
]

export const AR_CHANGE_DRIVER_EXCEL_COLUMNS: CfoArExcelColumn<ArCustomerChangeDriver>[] = [
  { header: 'Customer', value: (driver) => driver.customer || '', widthPx: 240 },
  { header: 'Prior A/R', value: (driver) => num(driver.previousEndingAr), format: 'currency', widthPx: 115 },
  { header: 'Selected A/R', value: (driver) => num(driver.endingAr), format: 'currency', widthPx: 115 },
  { header: 'Change', value: (driver) => num(driver.endingDelta), format: 'currency', widthPx: 115 },
  { header: 'Current change', value: (driver) => num(driver.currentDelta), format: 'currency', widthPx: 115 },
  { header: '1-90 change', value: (driver) => num(driver.under90Delta), format: 'currency', widthPx: 115 },
  { header: '>90 change', value: (driver) => num(driver.over90Delta), format: 'currency', widthPx: 115 },
]

export const AR_COLLECTABILITY_CUSTOMER_EXCEL_COLUMNS: CfoArExcelColumn<QuickBooksArCollectabilityCustomer>[] = [
  { header: 'Customer', value: (customer) => customer.customer || '', widthPx: 240 },
  { header: 'Open A/R', value: (customer) => num(customer.openAmount), format: 'currency', widthPx: 115 },
  { header: '>90 Days', value: (customer) => num(customer.over90Amount), format: 'currency', widthPx: 115 },
  { header: '>180 Days', value: (customer) => num(customer.over180Amount), format: 'currency', widthPx: 115 },
  { header: '>270 Days', value: (customer) => num(customer.over270Amount), format: 'currency', widthPx: 115 },
  { header: '>360 Days', value: (customer) => num(customer.over360Amount), format: 'currency', widthPx: 115 },
  { header: 'Reserve', value: (customer) => num(customer.expectedWriteOffAmount), format: 'currency', widthPx: 115 },
  { header: 'Worst days', value: (customer) => (customer.worstPastDueDays == null ? null : num(customer.worstPastDueDays)), format: 'days', widthPx: 105 },
]

export const AR_COLLECTABILITY_INVOICE_EXCEL_COLUMNS: CfoArExcelColumn<QuickBooksArCollectabilityInvoice>[] = [
  { header: 'Invoice', value: (invoice) => invoice.invoiceNumber || invoice.invoiceKey || '', widthPx: 130 },
  { header: 'Customer', value: (invoice) => invoice.customer || '', widthPx: 240 },
  { header: 'Due date', value: (invoice) => invoice.dueDate || '', widthPx: 110 },
  { header: 'Open A/R', value: (invoice) => num(invoice.openAmount), format: 'currency', widthPx: 115 },
  { header: 'Past due days', value: (invoice) => (invoice.daysPastDue == null ? null : num(invoice.daysPastDue)), format: 'days', widthPx: 115 },
  { header: 'Cohort', value: (invoice) => (invoice.thresholdDays ? `${invoice.thresholdDays}+` : 'N/A'), widthPx: 90 },
  { header: 'Recovery rate', value: (invoice) => formatArPercent(invoice.benchmarkRecoveryRate), widthPx: 110 },
  { header: 'Reserve', value: (invoice) => num(invoice.expectedWriteOffAmount), format: 'currency', widthPx: 115 },
]

/** Replace a projected response only with the full version of the same artifact. */
export function completeArAnalysis(
  summary: QuickBooksArTrendAnalysis,
  detail: QuickBooksArTrendAnalysis | null | undefined,
): QuickBooksArTrendAnalysis {
  if (!detail || detail.serving?.mode !== 'full'
    || !summary.serving?.artifactId || detail.serving.artifactId !== summary.serving.artifactId
    || detail.asOfDate !== summary.asOfDate) {
    throw new Error('A newer verified A/R report is available. Reload the report before opening detail.')
  }
  return detail
}
